import React from "react";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Footer from "../components/Footer";
import SectionHeader from "../components/SectionHeader";
import IconRender from "../components/IconRender";
import TrustedMarquee from "../components/TrustedMarquee";
import {
  SECTION_INTRO,
  SYSTEM_OVERVIEW,
  PILLARS,
  RESULTS,
  INTENT_SIGNALS,
  GROWTH_MACHINE,
  CONVERSION_BENEFITS,
  TARGET_INDUSTRIES,
  AUDIENCE_BUILDER,
  VISITOR_PILLARS,
  VISITOR_FEATURES,
  STATS,
  FUTURE_POINTS,
  PAIN_POINTS,
  MEDIA,
  BOOKING_URL,
} from "../mock";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  Sparkles,
  XCircle,
} from "lucide-react";

const Home = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <Hero />
      <TrustedMarquee />

      {/* Intro Section */}
      <section className="cga-section">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Real People • Real Time"
            title={SECTION_INTRO.title}
            highlight={SECTION_INTRO.titleHighlight}
          />
          <div className="max-w-3xl mx-auto text-center space-y-5">
            {SECTION_INTRO.paragraphs.map((p, i) => (
              <p
                key={i}
                className={`${
                  i === 1
                    ? "text-white text-xl md:text-2xl font-medium leading-snug"
                    : "cga-body"
                }`}
              >
                {p}
              </p>
            ))}
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="cga-card p-6 text-center"
              >
                <div className="text-3xl md:text-4xl font-extrabold text-[#5eead4]">
                  {s.value}
                </div>
                <div className="mt-1 text-sm text-white/65">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* System Overview */}
      <section id="solutions" className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            eyebrow={SYSTEM_OVERVIEW.eyebrow}
            title="AI Lead Generation"
            highlight="System"
          />
          <div className="max-w-3xl mx-auto text-center space-y-5">
            {SYSTEM_OVERVIEW.body.map((p, i) => (
              <p key={i} className="cga-body">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <div key={p.title} className="cga-card p-7">
                <div className="cga-icon-tile mb-5">
                  <IconRender name={p.icon} className="w-5 h-5" />
                </div>
                <h3 className="cga-h3 mb-3">{p.title}</h3>
                <p className="text-white/65 text-[15px] leading-relaxed">
                  {p.body}
                </p>
                {p.chips && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.chips.map((c) => (
                      <span
                        key={c}
                        className="text-xs font-semibold text-[#5eead4] border-b-2 border-[#5eead4]/70 pb-0.5"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                {p.list && (
                  <ul className="mt-5 space-y-2.5">
                    {p.list.map((it) => (
                      <li
                        key={it}
                        className="flex items-start gap-2.5 text-sm text-white/75"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#5eead4] mt-0.5 flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {p.footer && (
                  <p className="mt-5 text-sm text-white/55 italic">
                    {p.footer}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Results pill row */}
          <div className="mt-12">
            <div className="cga-card p-8 md:p-10">
              <div className="text-center cga-eyebrow mb-5">The Result</div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {RESULTS.map((r) => (
                  <span
                    key={r}
                    className="cga-pill text-sm py-2 px-4"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> {r}
                  </span>
                ))}
              </div>
              <p className="text-center text-white/70 mt-6 max-w-2xl mx-auto">
                Coastal Growth doesn't just generate leads.
                <br />
                <span className="text-[#5eead4] font-semibold">
                  It builds a complete revenue system designed to scale your
                  business.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team showcase strip with image */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative order-2 lg:order-1">
              <div
                className="absolute -inset-4 rounded-3xl opacity-50"
                style={{
                  background:
                    "radial-gradient(60% 60% at 30% 30%, rgba(94,234,212,0.25), transparent 70%)",
                  filter: "blur(24px)",
                }}
              />
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={MEDIA.team}
                  alt="Coastal Growth team collaborating"
                  className="w-full h-[460px] object-cover"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.85) 100%)",
                  }}
                />
                <div className="absolute bottom-5 left-5 right-5 grid grid-cols-3 gap-2">
                  <MiniStat label="Specialists" value="40+" />
                  <MiniStat label="Avg. ramp" value="14 days" />
                  <MiniStat label="Retention" value="96%" />
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="cga-eyebrow mb-4">An Entire Growth Team</div>
              <h2 className="cga-h2">
                <span className="text-white">One Platform. </span>
                <span className="text-[#5eead4]">Every Specialist You Need.</span>
              </h2>
              <p className="cga-body mt-5">
                Skip the agency carousel. Coastal Growth combines strategists,
                creators, engineers, and AI operators under one roof — all aligned
                to one goal: predictable revenue.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Paid ads strategists",
                  "Funnel & landing page designers",
                  "AI automation engineers",
                  "Video & creative editors",
                  "SEO & content specialists",
                  "Lifecycle email & SMS",
                ].map((s) => (
                  <li
                    key={s}
                    className="flex items-center gap-3 text-white/85"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#5eead4]" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Intent Data */}
      <section id="features" className="cga-section">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Intent Data"
            title="Intent Data That"
            highlight="Changes the Game"
            subtitle="While other agencies guess who might convert, Coastal Growth tracks live behavioral signals across billions of data points to uncover:"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INTENT_SIGNALS.map((s) => (
              <div
                key={s.label}
                className="cga-card p-5 flex items-center gap-4"
              >
                <div className="cga-icon-tile">
                  <IconRender name={s.icon} className="w-5 h-5" />
                </div>
                <div className="text-white font-semibold">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white text-xl md:text-2xl font-semibold tracking-tight">
              Income. Credit profile. Interests. Job title. Industry. Buying
              behavior.
            </p>
            <p className="cga-body mt-4 max-w-2xl mx-auto">
              No wasted ad spend. No generic targeting. No more hoping your ads
              land in the right feed.
            </p>
          </div>
        </div>
      </section>

      {/* Growth Machine */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Growth Machine"
            title="We Don't Just Send Leads."
            highlight="We Build Your Entire Growth Machine."
            subtitle="Once we identify your high-intent audience, we deploy:"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {GROWTH_MACHINE.map((g) => (
              <div key={g.title} className="cga-card p-8">
                <div className="cga-icon-tile mb-5">
                  <IconRender name={g.icon} className="w-5 h-5" />
                </div>
                <h3 className="cga-h3 mb-3">{g.title}</h3>
                <p className="text-white/65 leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>

          <div
            className="mt-10 cga-card p-8 text-center"
            style={{
              background:
                "linear-gradient(180deg, rgba(94,234,212,0.08), rgba(94,234,212,0.02))",
              borderColor: "rgba(94,234,212,0.25)",
            }}
          >
            <p className="text-white text-xl md:text-2xl font-semibold">
              Your business follows up{" "}
              <span className="text-[#5eead4]">instantly</span>. Every time.
            </p>
            <p className="text-white/65 mt-2">No leaks. No delays.</p>
          </div>
        </div>
      </section>

      {/* Conversion Rates */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            title="Lower Cost Per Lead."
            highlight="Higher Conversion Rates."
            subtitle="Because we target people who are already in buying mode, our clients see:"
          />

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {CONVERSION_BENEFITS.map((b) => (
              <div
                key={b.label}
                className="cga-card p-6 text-center hover:translate-y-[-2px]"
              >
                <div className="mx-auto cga-icon-tile rounded-full w-12 h-12 mb-3">
                  <IconRender name={b.icon} className="w-5 h-5" />
                </div>
                <div className="text-white font-semibold text-sm">
                  {b.label}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center cga-body">
            This isn't traditional lead generation.
            <br />
            <span className="text-[#5eead4] font-semibold">
              This is intent-driven revenue engineering.
            </span>
          </p>
        </div>
      </section>

      {/* Built for businesses */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Who It's For"
            title="Built for Businesses That Want to"
            highlight="Scale — Not Just Run Ads"
          />
          <div className="flex flex-wrap items-center justify-center gap-3">
            {TARGET_INDUSTRIES.map((t) => (
              <span
                key={t}
                className="px-5 py-2.5 rounded-full border border-white/10 bg-white/[0.02] text-white/85 text-sm hover:border-[#5eead4]/50 hover:text-[#5eead4] transition-colors"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Audience Builder */}
      <section id="case-studies" className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Audience Builder"
            title="Why Our Audience Builder"
            highlight="Works"
            subtitle="Precisely define your market. Scale without friction. Activate in seconds."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {AUDIENCE_BUILDER.map((a) => (
              <div key={a.title} className="cga-card p-7">
                <div className="cga-icon-tile mb-5">
                  <IconRender name={a.icon} className="w-5 h-5" />
                </div>
                <h3 className="cga-h3 mb-3">{a.title}</h3>
                <p className="text-white/65 text-[15px] leading-relaxed">
                  {a.body}
                </p>
              </div>
            ))}
          </div>

          {/* Mock dashboard previews */}
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DashboardMock
              title="Intent B2C - Consumer Targeting"
              caption="Target consumers by topics, income, marital status, and demographic filters"
              chips={[
                "Topics",
                "Income",
                "Marital Status",
                "Geography",
                "Recency",
              ]}
              metric={{ label: "Live Audience", value: "4.2M" }}
            />
            <DashboardMock
              title="Intent B2B - Business Targeting"
              caption="Target businesses by company revenue, topics, and B2B intent signals"
              chips={[
                "Industry",
                "Revenue",
                "Employee count",
                "Job title",
                "Tech stack",
              ]}
              metric={{ label: "Decision-Makers", value: "812K" }}
            />
          </div>

          {/* Big dashboard image preview */}
          <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10">
            <img
              src={MEDIA.dashboard}
              alt="Audience builder dashboard"
              className="w-full h-[420px] object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.9) 100%)",
              }}
            />
            <div className="absolute left-6 right-6 bottom-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <div className="cga-eyebrow mb-2">Audience Studio</div>
                <h3 className="text-white text-2xl md:text-3xl font-bold">
                  Build, deduplicate, and activate in one canvas.
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Google", "Meta", "LinkedIn", "Trade Desk"].map((p) => (
                  <span
                    key={p}
                    className="px-3 py-1.5 rounded-full text-xs text-white/85 border border-white/15 bg-black/40 backdrop-blur"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future of Lead Gen */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <div className="cga-card p-10 md:p-14 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-50"
              style={{
                background:
                  "radial-gradient(50% 60% at 80% 0%, rgba(94,234,212,0.15), transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="cga-eyebrow mb-3">Looking Ahead</div>
              <h2 className="cga-h2 mb-8">
                <span className="text-white">The Future of Lead Generation</span>
                <br />
                <span className="text-[#5eead4]">Is Intent + AI</span>
              </h2>
              <p className="cga-body max-w-2xl mb-8">
                The businesses that win over the next 5 years will:
              </p>
              <ul className="space-y-3 max-w-xl">
                {FUTURE_POINTS.map((p) => (
                  <li key={p} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#5eead4]" />
                    <span className="text-white/85">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-white/70">
                That's exactly what{" "}
                <span className="text-[#5eead4] font-semibold">
                  Coastal Growth Advisors
                </span>{" "}
                was built for.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visitor Identification */}
      <section className="cga-section pt-0">
        <div className="cga-container">
          <SectionHeader
            eyebrow="Visitor Identification"
            title="Know Who's Visiting."
            highlight="Capture More Revenue."
            subtitle="We transform anonymous website traffic into real, actionable intelligence — then deploy it across direct mail, paid ads, and email campaigns automatically."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VISITOR_PILLARS.map((v) => (
              <div key={v.title} className="cga-card p-6">
                <div className="cga-icon-tile mb-4">
                  <IconRender name={v.icon} className="w-5 h-5" />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">
                  {v.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {v.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <SectionHeader
              eyebrow="Power"
              title="The Power of"
              highlight="Visitor Identification"
              subtitle="Turn anonymous website traffic into actionable insights and real opportunities."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {VISITOR_FEATURES.map((f) => (
                <div key={f.title} className="cga-card p-7">
                  <div className="cga-icon-tile mb-4">
                    <IconRender name={f.icon} className="w-5 h-5" />
                  </div>
                  <h3 className="cga-h3 mb-2">{f.title}</h3>
                  <p className="text-white/65 text-[15px] leading-relaxed">
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white max-w-3xl mx-auto leading-tight">
              Stop losing opportunities from anonymous traffic.{" "}
              <span className="text-[#5eead4]">
                Start converting visitors into customers.
              </span>
            </h3>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="cga-section">
        <div className="cga-container">
          <div className="cga-card p-0 relative overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="relative hidden lg:block">
                <img
                  src={MEDIA.meeting}
                  alt="Strategy call"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.95) 100%)",
                  }}
                />
              </div>

              <div className="relative p-10 md:p-14">
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 80% 0%, rgba(94,234,212,0.18), transparent 70%)",
                  }}
                />
                <div className="relative">
                  <div className="cga-eyebrow mb-4">Get Started</div>
                  <h2 className="cga-h2">
                    <span className="text-white">Ready to Stop Guessing and</span>
                    <br />
                    <span className="text-[#5eead4]">Wasting Ad Spend?</span>
                  </h2>

                  <div className="mt-8 max-w-xl">
                    <p className="text-white/75 mb-4">If you're tired of:</p>
                    <ul className="space-y-2.5">
                      {PAIN_POINTS.map((p) => (
                        <li
                          key={p}
                          className="flex items-center gap-3 text-white/85"
                        >
                          <XCircle className="w-5 h-5 text-rose-400/80" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="mt-8 text-white text-xl font-semibold">
                    It's time for a smarter system.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-4">
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cga-cta-primary"
                    >
                      <Phone className="w-4 h-4" />
                      Book a Strategy Call
                      <ArrowRight className="w-4 h-4" />
                    </a>
                    <a href="#features" className="cga-cta-secondary">
                      See How It Works
                    </a>
                  </div>
                  <p className="mt-5 text-white/55 text-sm max-w-lg">
                    Let us show you exactly how many high-intent buyers are
                    searching in your market right now.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* Mock dashboard tile component */
const MiniStat = ({ label, value }) => (
  <div className="rounded-lg border border-white/10 bg-black/60 backdrop-blur-sm px-3 py-2 text-center">
    <div className="text-[10px] uppercase tracking-widest text-white/55">
      {label}
    </div>
    <div className="text-white font-bold text-sm mt-0.5">{value}</div>
  </div>
);

const DashboardMock = ({ title, caption, chips, metric }) => {
  return (
    <div className="cga-card overflow-hidden">
      <div className="p-5 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        </div>
        <div className="text-[11px] text-white/40 tracking-widest uppercase">
          Live
        </div>
      </div>
      <div className="p-7">
        <h3 className="text-white font-bold text-xl">{title}</h3>
        <p className="text-white/55 text-sm mt-1.5">{caption}</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span
              key={c}
              className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] text-white/70 border border-white/10"
            >
              {c}
            </span>
          ))}
        </div>

        <div className="mt-7 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/45">
              {metric.label}
            </div>
            <div className="text-4xl font-extrabold text-white mt-1">
              {metric.value}
            </div>
          </div>
          <BarSpark />
        </div>
      </div>
    </div>
  );
};

const BarSpark = () => {
  const heights = [22, 38, 30, 52, 44, 64, 56, 78, 68, 90];
  return (
    <div className="flex items-end gap-1.5 h-[90px]">
      {heights.map((h, i) => (
        <div
          key={i}
          className="w-2.5 rounded-sm"
          style={{
            height: `${h}%`,
            background: `linear-gradient(180deg, rgba(94,234,212,0.9), rgba(94,234,212,0.25))`,
          }}
        />
      ))}
    </div>
  );
};

export default Home;
